import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  getCommitteeApproval,
  type CommitteeApprovalResponse,
  type CommitteeApprovalMode,
  type CommitteeQuorumInfo,
} from '../services/ApplicationService';
import { useConfig } from '../context/ConfigContext';
import { hasData, getRolesFromToken, hasRole } from '../utils/Utils';
import { hasCommitteeMemberRealmRole } from '../utils/committeeMemberRole';
import { ROLES_CAP } from '../utils/Constants';

export interface UseCommitteeModeOptions {
  cuit?: string;
  applicationId?: string;
  /**
   * Si la solicitud está rechazada/finalizada el front no debe pedir aprobación;
   * pasarle false ahorra el GET y mantiene los flags coherentes.
   */
  enabled?: boolean;
}

export interface UseCommitteeModeResult {
  /**
   * Modo efectivo del flujo de comité.
   * - 'LEGACY': flujo por roles secuenciales (cliente Cementos / default).
   * - 'MEMBER_QUORUM': flujo por quórum de usuarios COMITE (cliente Innova,
   *   Task 50111). Sólo se considera MEMBER_QUORUM cuando el flag de config
   *   `isCommitteeMemberQuorumEnabled` está activo Y el backend devolvió ese mode.
   */
  mode: CommitteeApprovalMode;
  isMemberQuorumMode: boolean;
  /**
   * Datos crudos del último GET /committee/approve. En modo LEGACY se usa para
   * `roles`, `nextRole`, `hasNext`. En modo MEMBER_QUORUM se usa para `quorum`.
   * `null` mientras el endpoint no se haya llamado o haya fallado.
   */
  approvalData: CommitteeApprovalResponse | null;
  /** Atajo a `approvalData.quorum` cuando el modo es MEMBER_QUORUM; null en LEGACY. */
  quorum: CommitteeQuorumInfo | null;
  /**
   * Indica si el botón "Aprobar" debe estar habilitado para el usuario actual:
   * - LEGACY: true si el rol del usuario está en la lista pendiente (o si el flag
   *   `isMultipleApprovalInCommitteeEnabled` está apagado, en cuyo caso el flujo
   *   antiguo permite a cualquier rol del comité aprobar).
   * - MEMBER_QUORUM: true si el quórum auto aún no está alcanzado y el usuario
   *   no aprobó en el ciclo actual.
   */
  canApprove: boolean;
  /**
   * Solo aplica en MEMBER_QUORUM: true cuando se alcanzó el mínimo manual y el
   * usuario actual ya aprobó en el ciclo actual (puede empujar manualmente a INS).
   * El backend es la fuente de verdad; el front solo refleja el flag.
   */
  canSendToInstrumentation: boolean;
  /** Solo aplica en MEMBER_QUORUM: true si el usuario aprobó en el ciclo actual. */
  userAlreadyApproved: boolean;
  isLoading: boolean;
  /** Fuerza un refetch (útil tras aprobar / enviar a instrumentación). */
  refresh: () => void;
}

/**
 * Task 50111: hook que centraliza la decisión "¿qué modo de comité estamos viendo
 * y qué acciones puede ejecutar el usuario actual?". Lo crítico es que el front
 * NO duplique reglas de negocio del backend: solo refleja los flags
 * (`userAlreadyApproved`, `canSendToInstrumentation`, etc) que ms-application
 * devuelve dentro de `quorum`. El backend sigue siendo la fuente de verdad para
 * autorización y conteo.
 */
export const useCommitteeMode = ({
  cuit,
  applicationId,
  enabled = true,
}: UseCommitteeModeOptions): UseCommitteeModeResult => {
  const appConfig = useConfig();
  const isCommitteeMemberQuorumEnabled =
    appConfig.isCommitteeMemberQuorumEnabled === true;
  const isMultipleApprovalInCommitteeEnabled =
    appConfig.isMultipleApprovalInCommitteeEnabled === true;

  const [approvalData, setApprovalData] =
    useState<CommitteeApprovalResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refresh = useCallback(() => {
    setRefreshKey((n) => n + 1);
  }, []);

  /**
   * El GET /committee/approve sólo es útil cuando alguno de los dos flujos
   * activos lo necesita. En el flujo LEGACY antiguo (sin
   * `isMultipleApprovalInCommitteeEnabled`) no existía ese endpoint y los
   * botones siempre se mostraban; mantenemos esa compatibilidad.
   */
  const shouldFetchApproval =
    enabled &&
    !!cuit &&
    !!applicationId &&
    (isCommitteeMemberQuorumEnabled || isMultipleApprovalInCommitteeEnabled);

  useEffect(() => {
    if (!shouldFetchApproval) {
      setApprovalData(null);
      return;
    }
    setIsLoading(true);
    getCommitteeApproval(cuit as string, applicationId as string)
      .then((response: any) => {
        if (hasData(response) && response.status === 200) {
          setApprovalData(response.data as CommitteeApprovalResponse);
        } else {
          setApprovalData(null);
        }
      })
      .catch(() => setApprovalData(null))
      .finally(() => setIsLoading(false));
  }, [shouldFetchApproval, cuit, applicationId, refreshKey]);

  const mode: CommitteeApprovalMode = useMemo(() => {
    if (isCommitteeMemberQuorumEnabled && approvalData?.mode === 'MEMBER_QUORUM') {
      return 'MEMBER_QUORUM';
    }
    return 'LEGACY';
  }, [isCommitteeMemberQuorumEnabled, approvalData?.mode]);

  const isMemberQuorumMode = mode === 'MEMBER_QUORUM';
  const quorum = isMemberQuorumMode ? approvalData?.quorum ?? null : null;

  const canApprove = useMemo(() => {
    if (isCommitteeMemberQuorumEnabled && !approvalData) {
      return false;
    }
    if (hasRole(ROLES_CAP.admin)) {
      if (isMemberQuorumMode) {
        if (!quorum) return false;
        const autoReached = quorum.approvalsCount >= quorum.autoQuorum;
        return !autoReached && !quorum.userAlreadyApproved;
      }
      if (!isMultipleApprovalInCommitteeEnabled) {
        return true;
      }
      return (approvalData?.roles?.length ?? 0) > 0;
    }
    if (isMemberQuorumMode) {
      const userRoles = getRolesFromToken();
      if (!hasCommitteeMemberRealmRole(userRoles)) {
        return false;
      }
      if (!quorum) return false;
      // Si ya alcanzó el quórum auto el backend ya empujó a INS; no más aprobaciones.
      const autoReached = quorum.approvalsCount >= quorum.autoQuorum;
      return !autoReached && !quorum.userAlreadyApproved;
    }
    if (!isMultipleApprovalInCommitteeEnabled) {
      return true;
    }
    if (!approvalData?.roles?.length) return false;
    const userRolesArray = getRolesFromToken();
    return approvalData.roles.some((r) => userRolesArray.includes(r.code));
  }, [
    isCommitteeMemberQuorumEnabled,
    approvalData,
    isMemberQuorumMode,
    quorum,
    isMultipleApprovalInCommitteeEnabled,
    approvalData?.roles,
  ]);

  const canSendToInstrumentation = isMemberQuorumMode
    ? quorum?.canSendToInstrumentation === true
    : false;
  const userAlreadyApproved = isMemberQuorumMode
    ? quorum?.userAlreadyApproved === true
    : false;

  return {
    mode,
    isMemberQuorumMode,
    approvalData,
    quorum,
    canApprove,
    canSendToInstrumentation,
    userAlreadyApproved,
    isLoading,
    refresh,
  };
};

export default useCommitteeMode;
